import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import useTimer from 'src/common/hooks/useTimer';
import { FlexibleStepperContextProps } from 'src/components/FlexibleStepper/types';
import { useFlexibleStepper } from 'src/components/FlexibleStepper/useFlexibleStepper';
import { NODE_ENV } from 'src/config';
import { EventNamesMap, useAmplitude } from 'src/context/AmplitudeProvider';
import AuthStepperFooter from 'src/features/auth/presentation/components/AuthStepperFooter/AuthStepperFooter';
import AnimatedStepWrapper from 'src/features/auth/presentation/components/SignUpStepper/AnimatedStepWrapper';
import PhoneInputStep from 'src/features/auth/presentation/components/SignUpStepper/PhoneInputStep';
import VerificationOtpInput from 'src/features/auth/presentation/components/VerificationStepper/VerificationOtpInput';
import useAuthMutations from 'src/features/auth/presentation/hooks/useAuthMutations';
import useOtpVerification from 'src/features/verification/presentation/hooks/useOtpVerification';
import { VerificationChannels } from 'src/types/common';
import { FEATURE_FLAGS } from 'src/utils/growthBook/growthBook.constants';

interface PhoneVerificationStepProps {
  onSuccess?: (stepperContext: FlexibleStepperContextProps) => void;
  readOnly?: boolean;
}

export default function PhoneVerificationStep({
  onSuccess,
  readOnly,
}: PhoneVerificationStepProps) {
  const isWhatsappOtpEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ENABLE_WHATSAPP_OTP
  );
  const supportHref = useFeatureValue(FEATURE_FLAGS.SUPPORT_LINK, '');

  const [otpChannel] = useState<
    VerificationChannels.sms | VerificationChannels.whatsapp
  >(
    isWhatsappOtpEnabled
      ? VerificationChannels.whatsapp
      : VerificationChannels.sms
  );

  const [hasSentCode, setHasSentCode] = useState(false);

  const { values } = useFormikContext<{ phone_number: string }>();

  const { sendPhoneVerificationCodeMutation, verifyPhoneCodeMutation } =
    useOtpVerification();
  const { logoutMutation } = useAuthMutations();

  const { time, resetTimer, formattedTimer } = useTimer({
    enabled: hasSentCode,
    initialTime: NODE_ENV === 'development' ? 3000 : 30000,
  });

  const stepperContext = useFlexibleStepper();
  const { logEvent } = useAmplitude();

  const handleSendOtp = () => {
    logEvent(EventNamesMap.PhoneOtpRequestClick, {});
    sendPhoneVerificationCodeMutation.mutate(
      {
        to: values.phone_number,
        channel: otpChannel,
      },
      {
        onSuccess: () => {
          setHasSentCode(true);
          resetTimer();
        },
      }
    );
  };

  const handleVerifyOtp = (otp: string) => {
    logEvent(EventNamesMap.PhoneOtpVerificationSubmitted, {});
    verifyPhoneCodeMutation.mutate(
      {
        to: values.phone_number,
        verificationCode: otp,
        channel: otpChannel,
      },
      {
        onSuccess: () => {
          onSuccess?.(stepperContext);
        },
      }
    );
  };

  const handleResendCode = () => {
    handleSendOtp();
  };

  const enableRequestNewCode =
    (!time || time === 0) && !sendPhoneVerificationCodeMutation.isPending;

  const isSendingCode = sendPhoneVerificationCodeMutation.isPending;
  const enableLogout = stepperContext.isFirstStep && !hasSentCode;
  const backButtonLabel = !enableLogout ? 'Regresar' : 'Cerrar sesión';

  const handleLogout = async () => {
    logoutMutation.mutate();
  };

  const handleBack = () => {
    if (enableLogout) {
      return handleLogout();
    }

    if (hasSentCode) {
      return setHasSentCode(false);
    }

    stepperContext.moveBackward();
  };

  const handleSupportClick = () => {
    window.open(supportHref, '_blank', 'noopener,noreferrer');
  };

  return (
    <AnimatedStepWrapper>
      {hasSentCode ? (
        <VerificationOtpInput
          onSubmit={handleVerifyOtp}
          onResendCode={handleResendCode}
          enableRequestNewCode={enableRequestNewCode}
          timeToResend={formattedTimer}
          isError={verifyPhoneCodeMutation.isError}
          isPending={verifyPhoneCodeMutation.isPending}
          to={values.phone_number}
        />
      ) : (
        <PhoneInputStep
          onSubmit={handleSendOtp}
          title={'Necesitamos verificar tu número de teléfono'}
          description={`Enviaremos un código de seis dígitos por ${
            otpChannel === VerificationChannels.whatsapp ? 'WhatsApp' : 'SMS'
          } que deberás ingresar en el siguiente paso.`}
          submitLabel={
            isSendingCode
              ? 'Enviando...'
              : `Enviar código ${
                  enableRequestNewCode ? '' : `(${formattedTimer})`
                }`
          }
          disableNext={isSendingCode || !enableRequestNewCode}
          readOnly={readOnly}
        />
      )}
      <AuthStepperFooter
        backButton={{
          color: 'greyText',
          label: backButtonLabel,
          onClick: handleBack,
          variant: 'text',
        }}
        nextButton={{
          color: 'error',
          label: 'Ayuda',
          onClick: handleSupportClick,
          variant: 'text',
        }}
      />
    </AnimatedStepWrapper>
  );
}
